import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money, formatTime, minutesSince } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardList } from "lucide-react";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import NewOrderDialog from "@/components/NewOrderDialog";

export default function Orders() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("open");

  const load = async () => {
    const { data } = await api.get("/orders");
    setOrders(data);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const filtered = orders.filter((o) => {
    if (tab === "open") return o.status === "open" || o.status === "served";
    if (tab === "paid") return o.status === "paid";
    return true;
  });

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{t("orders")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{filtered.length} · {tab}</p>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-order-btn" className="active:scale-95">
          <Plus className="w-4 h-4 mr-2" /> {t("newOrder")}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="tab-open">{t("status_open")}</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">{t("status_paid")}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">{lang === "tr" ? "Tümü" : "Alle"}</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {filtered.length === 0 && (
            <div className="text-center py-16 border border-dashed rounded-lg text-muted-foreground">
              <ClipboardList className="w-8 h-8 mx-auto opacity-40" />
              <div className="mt-3 text-sm">{t("empty")}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-card-${o.id}`}>
                <Card className="border shadow-none hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-mono text-xs text-muted-foreground">#{o.id.slice(0, 6)}</div>
                        <div className="font-display font-semibold mt-1">
                          {o.table_id ? "Masa" : (lang === "tr" ? "Paket" : "Take-away")}
                        </div>
                      </div>
                      <Badge variant={o.status === "paid" ? "secondary" : "default"}>
                        {t(`status_${o.status}`)}
                      </Badge>
                    </div>
                    <div className="mt-3 flex justify-between items-end">
                      <div className="text-xs text-muted-foreground font-mono">
                        {formatTime(o.created_at)} · {minutesSince(o.created_at)}′
                      </div>
                      <div className="font-mono font-semibold text-lg">{money(o.total)}</div>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground truncate">
                      {o.items?.length || 0} × items
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <NewOrderDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => nav(`/orders/${id}`)} />
    </div>
  );
}
